"use client";

import { Form, Formik } from "formik";
import { useSearchParams } from "next/navigation";
import { FC, useState } from "react";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, Input, Typography } from "@/shared/ui";
import EyeIcon from "@/svgs/eye.svg";

import { useResetPassword } from "./hooks/reset-password.hook";
import styles from "./reset-password-form.module.scss";
import { ResetPasswordFormScheme } from "./reset-password-form.types";

const validationScheme = object({
  newPassword: string().min(8, "Must be at least 8 characters"),
  confirmedPassword: string().min(1, "Field is required"),
}).refine(({ newPassword, confirmedPassword }) => newPassword === confirmedPassword, {
  message: "The confirmation password doesn't match",
  path: ["confirmedPassword"],
});

const ResetPasswordForm: FC = () => {
  const searchParams = useSearchParams();

  const { mutate, isSuccess, isPending } = useResetPassword();

  const code = searchParams.get("code");
  const email = searchParams.get("email");

  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true);
  const [isConfirmedPasswordHidden, setIsConfirmedPasswordHidden] = useState<boolean>(true);

  const onSubmitHandler = ({ newPassword }: ResetPasswordFormScheme) => {
    if (code && email) {
      mutate({ code, email, newPassword });
    }
  };

  return (
    <div className={styles.resetPasswordForm}>
      <div className={styles.header}>
        <Typography bold variant="large-title">
          Set New Password
        </Typography>
        <Typography variant="caption-1">Create a new password to regain access</Typography>
      </div>

      <Formik<ResetPasswordFormScheme>
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{ newPassword: "", confirmedPassword: "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, isValid, setFieldValue, setFieldTouched, validateField }) => (
          <Form className={styles.form}>
            <Input
              required
              placeholder="Enter new password"
              value={values.newPassword}
              message={errors.newPassword}
              variant={errors.newPassword ? "error" : undefined}
              type={isPasswordHidden ? "password" : "text"}
              endContent={
                <EyeIcon className={styles.passwordEyeIcon} onClick={() => setIsPasswordHidden((prev) => !prev)} />
              }
              onBlur={() => {
                validateField("newPassword");
                setFieldTouched("newPassword");
              }}
              onValueChange={async (value) => {
                await setFieldValue("newPassword", value);
                errors.newPassword && (await validateField("newPassword"));
              }}
            />
            <Input
              required
              placeholder="Confirm password"
              value={values.confirmedPassword}
              message={errors.confirmedPassword}
              variant={errors.confirmedPassword ? "error" : undefined}
              type={isConfirmedPasswordHidden ? "password" : "text"}
              endContent={
                <EyeIcon className={styles.passwordEyeIcon} onClick={() => setIsConfirmedPasswordHidden((prev) => !prev)} />
              }
              onBlur={() => {
                validateField("confirmedPassword");
                setFieldTouched("confirmedPassword");
              }}
              onValueChange={async (value) => {
                await setFieldValue("confirmedPassword", value);
                errors.confirmedPassword && (await validateField("confirmedPassword"));
              }}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={isPending || isSuccess || !isValid || Object.values(values).some((v) => !v)}
            >
              {"Reset Password"}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export { ResetPasswordForm };
